import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { StatementEntryView, StatementOpenInvoiceView } from '@lims/shared-types';
import { statementsApi } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DataTable,
  DateField,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  TextField,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { Facts, RecordGrid, RecordNumber } from '../clients/record-layout.js';

/**
 * A client's statement (D155): opening balance, this period's movements,
 * closing balance — built ONLY from the client's account entries.
 *
 * EVERY OMITTED FILTER IS A DEFAULT THE BACKEND CHOSE, NAMED IN
 * `defaultsApplied`. This page renders that list rather than silently
 * assuming the reader knows the laboratory's currency or "the last year" —
 * a default is not the same as something the reader asked for.
 *
 * PRINTED WITH THE BROWSER. No send, no record of a copy: `window.print()`
 * and nothing else, exactly the honesty a quotation's print already keeps.
 */
export function StatementPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [currency, setCurrency] = useState('');

  const statement = useQuery({
    queryKey: ['statement', id, { from, to, currency }],
    queryFn: () =>
      statementsApi.get(id, {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(currency.trim() ? { currency: currency.trim().toUpperCase() } : {}),
      }),
    enabled: Boolean(id),
  });

  const rowColumns: readonly DataColumn<StatementEntryView>[] = [
    {
      key: 'entryDate',
      header: t('statements.entryDate'),
      render: (row) => <span className="whitespace-nowrap">{formatDate(row.entryDate, { locale })}</span>,
    },
    {
      key: 'kind',
      header: t('statements.kindLabel'),
      render: (row) => t(`accountEntryKind.${row.kind}`),
    },
    {
      key: 'document',
      header: t('statements.document'),
      render: (row) =>
        row.invoiceId ? (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => navigate(`/invoices/${row.invoiceId}`)}
          >
            {row.documentNumber ?? t('common.open')}
          </ActionButton>
        ) : row.paymentId ? (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => navigate(`/payments/${row.paymentId}`)}
          >
            {row.documentNumber ?? t('common.open')}
          </ActionButton>
        ) : (
          <span className="text-default-600">
            {row.documentNumber ?? t('common.notRecorded')}
          </span>
        ),
    },
    {
      key: 'debit',
      header: t('statements.debit'),
      align: 'end',
      render: (row) =>
        row.debitMinor
          ? <span dir="ltr" className="measure">{formatMoney(row.debitMinor, { locale, currency: statement.data?.currency ?? 'SAR' })}</span>
          : null,
    },
    {
      key: 'credit',
      header: t('statements.credit'),
      align: 'end',
      render: (row) =>
        row.creditMinor
          ? <span dir="ltr" className="measure">{formatMoney(row.creditMinor, { locale, currency: statement.data?.currency ?? 'SAR' })}</span>
          : null,
    },
    {
      key: 'balance',
      header: t('statements.runningBalance'),
      align: 'end',
      render: (row) => (
        <span className="measure font-medium" dir="ltr">
          {formatMoney(row.balanceMinor, { locale, currency: statement.data?.currency ?? 'SAR' })}
        </span>
      ),
    },
  ];

  const openColumns: readonly DataColumn<StatementOpenInvoiceView>[] = [
    {
      key: 'number',
      header: t('invoices.number'),
      render: (invoice) => (
        <RecordNumber to={`/invoices/${invoice.id}`}>{invoice.number}</RecordNumber>
      ),
    },
    {
      key: 'issueDate',
      header: t('invoices.issueDate'),
      render: (invoice) => formatDate(invoice.issueDate, { locale }),
    },
    {
      key: 'dueDate',
      header: t('invoices.dueDate'),
      render: (invoice) => (
        <span className="flex items-center gap-inline">
          {formatDate(invoice.dueDate, { locale })}
          {invoice.overdue ? (
            <StatusBadge label={t('invoices.overdue')} tone="warning" />
          ) : null}
        </span>
      ),
    },
    {
      key: 'balance',
      header: t('invoices.balance'),
      align: 'end',
      render: (invoice) => (
        <span dir="ltr" className="measure">
          {formatMoney(invoice.balanceMinor, { locale, currency: statement.data?.currency ?? 'SAR' })}
        </span>
      ),
    },
  ];

  if (statement.isLoading) return <LoadingState label={t('common.loading')} />;
  if (statement.error) {
    return <ErrorState error={statement.error} onRetry={() => void statement.refetch()} />;
  }
  const record = statement.data;
  if (!record) return null;

  const money = (minor: number) => formatMoney(minor, { locale, currency: record.currency });
  const amount = (minor: number, strong = false) => (
    <span dir="ltr" className={`measure${strong ? ' font-semibold' : ''}`}>
      {money(minor)}
    </span>
  );

  return (
    <>
      <PageHeader
        title={t('statements.title', {
          client: record.client.name ?? record.client.number,
        })}
        subtitle={t('statements.periodLabel', {
          from: formatDate(record.from, { locale }),
          to: formatDate(record.to, { locale }),
        })}
        breadcrumbs={[
          { label: t('nav.clients'), to: '/clients' },
          { label: record.client.name ?? record.client.number, to: `/clients/${id}` },
          { label: t('statements.breadcrumbLabel') },
        ]}
        actions={
          <ActionBar>
            <ActionButton priority="secondary" onPress={() => window.print()}>
              {t('statements.print')}
            </ActionButton>
          </ActionBar>
        }
      />

      <div className="mb-stack">
        <Alert tone="info" title={t('invoices.notTaxInvoiceNotice')} />
      </div>

      {record.defaultsApplied.length > 0 ? (
        <div className="mb-stack">
          <Alert
            tone="info"
            title={t('statements.defaultsAppliedNotice', {
              defaults: record.defaultsApplied
                .map((key) => t(`statements.defaults.${key}`, { defaultValue: key }))
                .join(', '),
            })}
          />
        </div>
      ) : null}

      {/* The period being read: chosen here, never printed. */}
      <div className="mb-stack print:hidden">
        <Panel>
          <div className="grid gap-stack sm:grid-cols-3">
            <DateField label={t('statements.from')} value={from} onChange={setFrom} />
            <DateField label={t('statements.to')} value={to} onChange={setTo} />
            <TextField
              label={t('statements.currency')}
              description={t('statements.currencyHint')}
              value={currency}
              onChange={setCurrency}
            />
          </div>
        </Panel>
      </div>

      <RecordGrid
        side={
          // The account in figures: the server's, never recomputed here.
          <Panel title={t('common.details')}>
            <Facts
              items={[
                {
                  label: t('statements.currency'),
                  value: (
                    <span dir="ltr" className="measure">
                      {record.currency}
                    </span>
                  ),
                },
                { label: t('statements.asOf'), value: formatDate(record.asOf, { locale }) },
                {
                  label: t('statements.openingBalance'),
                  value: amount(record.openingBalanceMinor),
                },
                {
                  label: t('statements.closingBalance'),
                  value: amount(record.closingBalanceMinor, true),
                },
                {
                  label: t('statements.unallocatedCredit'),
                  value: amount(record.unallocatedCreditMinor),
                },
                {
                  label: t('statements.totalInvoiced'),
                  value: amount(record.totals.invoicedMinor),
                },
                { label: t('statements.totalPaid'), value: amount(record.totals.paidMinor) },
                {
                  label: t('statements.totalCancelled'),
                  value: amount(record.totals.cancelledMinor),
                },
              ]}
            />
          </Panel>
        }
      >
        <Panel title={t('statements.movements')}>
          <DataTable
            label={t('statements.movements')}
            columns={rowColumns}
            rows={record.rows}
            rowKey={(row) =>
              `${row.kind}-${row.invoiceId ?? row.paymentId ?? row.adjustmentId ?? row.entryDate}-${row.balanceMinor}`
            }
            emptyContent={t('statements.noMovements')}
          />
        </Panel>

        <Panel title={t('statements.openInvoices')}>
          <DataTable
            label={t('statements.openInvoices')}
            columns={openColumns}
            rows={record.openInvoices}
            rowKey={(invoice) => invoice.id}
            emptyContent={t('statements.noOpenInvoices')}
          />
        </Panel>
      </RecordGrid>
    </>
  );
}
